/**
 * Medical Leave Types for MEV-Med Frontend
 * Type definitions for Medical Leaves (Licenças Médicas) management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de Licenças Médicas
 */

/**
 * Medical Leave entity interface
 */
export interface MedicalLeave {
  id: number;
  employeeId: number;
  employeeName: string;
  companyId: number;
  companyName: string;
  departmentName: string;
  occupationName: string;
  leaveReason: string;
  doctorName: string;
  doctorUf?: string | null;
  doctorCrm: string;
  cidCode: string;
  cidDescription: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  returnRestrictions?: string;
  hasCertificate: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Form data for creating/updating medical leaves
 */
export interface MedicalLeaveFormData {
  employeeId: number;
  leaveReason: string;
  doctorName: string;
  doctorUf?: string | null;
  doctorCrm: string;
  cidCode: string;
  cidDescription?: string;
  startDate: string;
  endDate: string;
  returnRestrictions?: string;
}

/**
 * Filters for listing medical leaves
 */
export interface MedicalLeaveFilters {
  search?: string;
  companyId?: number;
  employeeId?: number;
  cidCode?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Response for list endpoint with pagination
 */
export interface MedicalLeaveListResponse {
  medicalLeaves: MedicalLeave[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Certificate info
 */
export interface CertificateInfo {
  id: number;
  fileName: string;
  fileExtension: string;
  filePath: string;
}

/**
 * Employee for leave selection
 */
export interface EmployeeForLeave {
  id: number;
  name: string;
  cpf: string;
  companyId: number;
  companyName: string;
  departmentName: string;
  occupationName: string;
}

/**
 * CID code info
 */
export interface CidCodeInfo {
  code: string;
  description: string;
}

/**
 * List of valid Brazilian UFs
 */
export const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO'
];

/**
 * List of eSocial leave reasons
 */
export const LEAVE_REASONS = [
  // Acidentes e Doenças
  'Acidente/doença do trabalho',
  'Novo afastamento decorrente do mesmo acidente/doença do trabalho dentro de 60 dias',
  'Acidente/doença não relacionada ao trabalho',
  'Novo afastamento decorrente do mesmo acidente/doença não relacionado ao trabalho dentro de 60 dias',

  // Licença Maternidade
  'Licença maternidade',
  'Licença maternidade - 120 dias',
  'Licença maternidade - 120 dias e suas prorrogações/antecipações',
  'Licença maternidade - 121 dias a 180 dias',
  'Licença maternidade - 180 dias',
  'Licença maternidade - Prorrogação por 60 dias',
  'Licença maternidade - Antecipação e/ou prorrogação mediante atestado médico',
  'Licença maternidade - Afastamento temporário por motivo de aborto não criminoso',
  'Licença maternidade - Afastamento temporário por motivo de licença-maternidade decorrente de adoção ou guarda judicial de criança',
  'Licença maternidade - Afastamento temporário por motivo de licença-maternidade para o cônjuge sobrevivente ou decorrente de adoção ou de guarda judicial de criança',

  // Licenças Diversas
  'Licença remunerada - Lei',
  'Licença não remunerada ou sem vencimento',
  'Afastamento/licença de servidor público ou militar prevista em estatuto',
  'Acompanhamento - Licença para acompanhamento de membro da família enfermo',

  // Afastamentos Especiais
  'Aposentadoria por invalidez',
  'Cárcere',
  'Cargo eletivo - Candidato a cargo eletivo - Art. 25',
  'Cargo eletivo - Candidato a cargo eletivo - Art. 1°',
  'Cessão/Requisição',
  'Gozo de férias ou recesso - Afastamento temporário para o gozo de férias ou recesso',
  'Mandato eleitoral - Afastamento temporário para o exercício de mandato eleitoral',
  'Mandato sindical - Afastamento temporário para exercício de mandato sindical',
  'Mulher vítima de violência - Art. 9º',
  'Qualificação - Afastamento por suspensão do contrato de acordo com o art. 476-A da CLT',
  'Qualificação - Afastamento por suspensão do contrato de acordo com o art. 15 da Lei 14.457/2022',
  'Qualificação - Afastamento por suspensão do contrato de acordo com o art. 17 da Lei 14.457/2022',
  'Representante sindical - Afastamento pelo tempo que se fizer necessário',
  'Serviço militar - Afastamento temporário para prestar serviço militar obrigatório',
  'Suspensão disciplinar - Art. 474 da CLT',
  'Servidor público em disponibilidade',
  'Inatividade do trabalhador avulso (portuário ou não portuário) por período superior a 90 dias',
  'Afastamento temporário de exercente de mandato eletivo para cargo em comissão',
  'Suspensão temporária do contrato de trabalho nos termos da Lei 14.020/2020',
  'Suspensão temporária do contrato de trabalho nos termos do Programa Emergencial de Manutenção do Emprego e da Renda',
  'Impedimento de concorrência à escala para trabalho avulso',
  'Suspensão de pagamento de servidor público por não recadastramento',
  'Exercício em outro órgão de servidor ou empregado público cedido',
  'Afastamento do empregado para participar de atividade do Conselho Curador do FGTS - Art. 65',
  'Participação de empregado no Conselho Nacional de Previdência Social - CNPS - Art. 3º da Lei 8.213/1991'
];

/**
 * Props for UI components
 */
export interface MedicalLeaveTableProps {
  medicalLeaves: MedicalLeave[];
  onEdit: (medicalLeave: MedicalLeave) => void;
  onDelete: (id: number) => void;
  onViewCertificate?: (medicalLeave: MedicalLeave) => void;
  loading?: boolean;
}

export interface MedicalLeaveFormProps {
  initialData?: MedicalLeave;
  employee?: EmployeeForLeave;
  onSubmit: (data: MedicalLeaveFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export interface MedicalLeaveFiltersProps {
  filters: MedicalLeaveFilters;
  onChange: (filters: MedicalLeaveFilters) => void;
  onSearch: () => void;
}

/**
 * Helper function to calculate total days between two dates (inclusive)
 */
export function calculateTotalDays(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays + 1;
}

/**
 * Helper function to format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
}

/**
 * Helper function to format CRM display
 */
export function formatCrm(crm: string, uf?: string | null): string {
  if (uf) return `${crm}/${uf}`;
  return crm;
}
